// sidebar has been updated as per the new changes.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  Group,
  Engineering,
  FormatListBulleted,
  Payment,
  Campaign,
  Build,
  Handshake,
  Share,
  ErrorOutline,
  Collections,
  EventNote,
  HowToReg,
  PersonAddAlt,
  HomeRepairService,
  Calculate,
  Compare,
  ExpandLess,
  ExpandMore,
  Logout,
  Notifications,
  BarChart,
  Category,
  ShoppingBag,
  LocalMall,
  CancelPresentationOutlined,
  Search,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Images } from '../../assets';
import withUserAuth from '../../hoc/withUserAuth';
import { useAuth } from '../../store/AuthContext';
import { useGlobalSearch } from '../../contexts/GlobalSearchContext';
import {
  AdminModule,
  canAccessModule,
  getModuleForPath,
  getResolvedPermissions,
} from '../../utils/adminPermissions';
import { AdminPanelSettings } from '@mui/icons-material';
import { getActiveMenuPath } from '../../utils/bookingNavigation';
import { APP_SHELL_HEADER_HEIGHT } from './layoutConstants';

const DRAWER_WIDTH = 210;
const COLLAPSED_WIDTH = 70;
const SIDEBAR_ICON_WIDTH = 44;
const LOGO_SIZE = 38;

const BRAND_TEXT_SX = {
  fontWeight: 700,
  fontSize: '1.0625rem',
  lineHeight: 1.1,
  color: '#FFFFFF',
  letterSpacing: '0.04em',
  fontFamily: 'inherit',
  userSelect: 'none',
} as const;

const SidebarBrand: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      width: '100%',
      height: 40,
    }}
  >
    <Box
      sx={{
        width: collapsed ? LOGO_SIZE : SIDEBAR_ICON_WIDTH,
        minWidth: collapsed ? LOGO_SIZE : SIDEBAR_ICON_WIDTH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={Images.ACDOCTORLOGO}
        alt="AC Doctor"
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'block',
          objectFit: 'cover',
        }}
      />
    </Box>
    {!collapsed && (
      <Typography noWrap sx={BRAND_TEXT_SX}>
        AC Doctor
      </Typography>
    )}
  </Box>
);

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { title: 'Dashboard', icon: <Dashboard />, path: '/home' },
  { title: 'Enquires', icon: <FormatListBulleted />, path: '/enquires' },
  {
    title: 'Cancelled',
    icon: <CancelPresentationOutlined />,
    path: '/cancelled',
  },
  { title: 'Payments', icon: <Payment />, path: '/payments' },
  { title: 'Bookings', icon: <FormatListBulleted />, path: '/bookings' },
  { title: 'Users', icon: <Group />, path: '/customers' },
  {
    title: 'Technicians',
    icon: <Engineering />,
    children: [
      {
        title: 'Attendance',
        icon: <HowToReg />,
        path: '/technicians/attendance',
      },
      {
        title: 'All Technicians',
        icon: <Engineering />,
        path: '/technicians',
      },

      {
        title: 'ACD Technician',
        icon: <Engineering />,
        path: '/technicians/acd-technicians',
      },
      {
        title: 'ACD Helpers',
        icon: <Engineering />,
        path: '/technicians/acd-helper',
      },
      {
        title: 'Contractor',
        icon: <Engineering />,
        path: '/technicians/contractor',
      },
      {
        title: 'Request Tools',
        icon: <HomeRepairService />,
        path: '/technicians/tool-request',
      },
      {
        title: 'Leaves',
        icon: <EventNote />,
        path: '/manage/leaves',
      },
      {
        title: 'Holidays',
        icon: <EventNote />,
        path: '/manage/holidays',
      },
      {
        title: 'New Join',
        icon: <PersonAddAlt />,
        path: '/technicians/new-join',
      },
    ],
  },
  { title: 'Promotions', icon: <Notifications />, path: '/promotions' },
  {
    title: 'Leads',
    icon: <BarChart />,
    children: [
      { title: 'Leads Management', icon: <Campaign />, path: '/leads' },
    ],
  },
  {
    title: 'Partners',
    icon: <Handshake />,
    children: [
      { title: 'All Partners', icon: <Group />, path: '/channel-partners' },
      {
        title: 'Identity Review',
        icon: <HowToReg />,
        path: '/channel-partners/identity',
      },
    ],
  },
  {
    title: 'Manage',
    icon: <Build />,
    children: [
      { title: 'Services', icon: <Build />, path: '/manage/services' },
      { title: 'Coupons', icon: <Campaign />, path: '/manage/coupons' },
      { title: 'Banners', icon: <Collections />, path: '/manage/banners' },
      {
        title: 'Product',
        icon: <ShoppingBag />,
        path: '/Product',
      },
      { title: 'Material', icon: <Build />, path: '/manage/material' },
      { title: 'Taxation', icon: <Build />, path: '/manage/taxation' },
      { title: 'Tool', icon: <Build />, path: '/manage/tools' },
      // {
      //   title: 'Errorcodes',
      //   icon: <ErrorOutline />,
      //   path: '/manage/brand-errors',
      // },
    ],
  },
  {
    title: 'Requests',
    icon: <Handshake />,
    children: [
      { title: 'AMC', icon: <Handshake />, path: '/request/amc' },
      {
        title: 'Copper Piping',
        icon: <Share />,
        path: '/request/copperpiping',
      },
      {
        title: 'Free Consultancy',
        icon: <Handshake />,
        path: '/request/free-consultancy',
      },
      {
        title: 'Purchase',
        icon: <LocalMall />,
        path: '/purchase',
      },
      {
        title: 'Sell Old AC',
        icon: <ShoppingBag />,
        path: '/request/sell-old-ac',
      },
    ],
  },
  {
    title: 'Utility',
    icon: <Category />,
    children: [
      {
        title: 'Error Codes',
        icon: <ErrorOutline />,
        path: '/utility/error-codes',
      },
      {
        title: 'Tonnage Calculator',
        icon: <Calculate />,
        path: '/utility/tonnage-calculator',
      },
      { title: 'Comparison', icon: <Compare />, path: '/utility/comparison' },
    ],
  },
  {
    title: 'Admin Roles',
    icon: <AdminPanelSettings />,
    path: '/admin-staff',
  },
];

const normalizeMenuPath = (path?: string) => {
  if (!path) return '/home';
  return path.startsWith('/') ? path : `/${path}`;
};

const pathMatchesPrefix = (pathname: string, normalizedPath: string) =>
  pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`);

/**
 * Among sibling menu paths, only the longest matching path is active
 * (e.g. /technicians/contractor must not also highlight /technicians).
 */
const isMenuItemActive = (
  pathname: string,
  menuPath?: string,
  siblingPaths: string[] = [],
): boolean => {
  if (!menuPath) return false;
  const normalized = normalizeMenuPath(menuPath);
  if (!pathMatchesPrefix(pathname, normalized)) return false;

  const candidates = (siblingPaths.length > 0 ? siblingPaths : [menuPath]).map(
    (p) => normalizeMenuPath(p),
  );

  const matching = candidates.filter((p) => pathMatchesPrefix(pathname, p));
  if (!matching.length) return false;

  const bestMatch = matching.reduce((longest, current) =>
    current.length > longest.length ? current : longest,
  );
  return bestMatch === normalized;
};

const getItemModule = (path?: string): AdminModule => {
  return getModuleForPath(normalizeMenuPath(path));
};

const filterMenuByPermissions = (
  items: MenuItem[],
  permissions: ReturnType<typeof useAuth>['authState']['permissions'],
): MenuItem[] => {
  return items
    .map((item) => {
      if (item.children?.length) {
        const children = filterMenuByPermissions(item.children, permissions);
        if (!children.length) return null;
        return { ...item, children };
      }
      const moduleName = getItemModule(item.path);
      if (!canAccessModule(permissions, moduleName, 'view')) return null;
      return item;
    })
    .filter(Boolean) as MenuItem[];
};

const Sidebar: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const permissions = useMemo(
    () => getResolvedPermissions(authState),
    [
      authState.role,
      authState.department,
      authState.userType,
      authState.token,
      authState.useCustomPermissions,
      authState.permissions,
      authState.customPermissions,
    ],
  );
  const visibleMenuItems = useMemo(
    () => filterMenuByPermissions(menuItems, permissions),
    [permissions],
  );
  const { openSearch } = useGlobalSearch();
  const searchShortcut =
    typeof navigator !== 'undefined' &&
    navigator.platform.toUpperCase().includes('MAC')
      ? '⌘K'
      : 'Ctrl+K';

  const [collapsed, setCollapsed] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const manuallyClosedMenus = useRef<Set<string>>(new Set());
  const activeMenuPath = getActiveMenuPath(location.pathname, location.state);

  const findParentForPath = (pathname: string) => {
    const topLevelPaths = visibleMenuItems
      .map((i) => i.path)
      .filter(Boolean) as string[];

    return visibleMenuItems.find((item) => {
      const childPaths =
        (item.children
          ?.map((child) => child.path)
          .filter(Boolean) as string[]) ?? [];
      if (isMenuItemActive(pathname, item.path, topLevelPaths)) return true;
      if (item.children) {
        return item.children.some((child) =>
          isMenuItemActive(pathname, child.path, childPaths),
        );
      }
      return false;
    });
  };

  useEffect(() => {
    manuallyClosedMenus.current.clear();
    const parent = findParentForPath(activeMenuPath);
    if (parent) {
      setOpenMenu(parent.title);
    }
  }, [activeMenuPath, visibleMenuItems]);

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open
      sx={{
        width: drawerWidth,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          backgroundColor: '#181818',
          color: '#fff',
          overflowX: 'hidden',
          transition: 'width 0.3s',
        },
      }}
    >
      <Box
        onClick={() => setCollapsed(!collapsed)}
        role="button"
        aria-label="AC Doctor — toggle sidebar"
        sx={{
          mx: 1,
          px: collapsed ? 0 : 2,
          height: APP_SHELL_HEADER_HEIGHT,
          minHeight: APP_SHELL_HEADER_HEIGHT,
          maxHeight: APP_SHELL_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <SidebarBrand collapsed={collapsed} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <List sx={{ pt: 0.5 }}>
        {visibleMenuItems.map((item) => {
          const hasChildren = Boolean(item.children);
          const topLevelPaths = visibleMenuItems
            .map((i) => i.path)
            .filter(Boolean) as string[];
          const childPaths = item.children
            ?.map((child) => child.path)
            .filter(Boolean) as string[] | undefined;
          const isActive = isMenuItemActive(
            activeMenuPath,
            item.path,
            topLevelPaths,
          );

          return (
            <React.Fragment key={item.title}>
              <Tooltip title={collapsed ? item.title : ''} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (hasChildren) {
                      const firstChild = item.children?.find((child) =>
                        canAccessModule(
                          permissions,
                          getItemModule(child.path),
                          'view',
                        ),
                      );
                      if (collapsed) {
                        setCollapsed(false);
                        setOpenMenu(item.title);
                        if (firstChild?.path) {
                          navigate(normalizeMenuPath(firstChild.path));
                        }
                        return;
                      }
                      const isClosing = openMenu === item.title;
                      if (isClosing) {
                        manuallyClosedMenus.current.add(item.title);
                        setOpenMenu(null);
                        return;
                      }
                      manuallyClosedMenus.current.delete(item.title);
                      setOpenMenu(item.title);
                      const onChildRoute = item.children?.some((child) =>
                        isMenuItemActive(
                          activeMenuPath,
                          child.path,
                          childPaths ?? [],
                        ),
                      );
                      if (firstChild?.path && !onChildRoute) {
                        navigate(normalizeMenuPath(firstChild.path));
                      }
                      return;
                    }
                    navigate(normalizeMenuPath(item.path));
                  }}
                  sx={{
                    mx: 1,
                    borderRadius: 3,
                    // backgroundColor: isActive ? '#2a2a2a' : 'transparent',
                    border: isActive
                      ? '1px solid #d32f2f'
                      : '1px solid transparent',

                    '&:hover': {
                      backgroundColor: '#2a2a2a',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#E31E25', minWidth: '35px' }}>
                    {item.icon}
                  </ListItemIcon>

                  {!collapsed && (
                    <>
                      <ListItemText primary={item.title} />
                      {hasChildren &&
                        (openMenu === item.title ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        ))}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {hasChildren && !collapsed && (
                <Collapse
                  in={openMenu === item.title}
                  timeout={250}
                  unmountOnExit
                >
                  <List>
                    {item.children?.map((child) => {
                      const childPath = normalizeMenuPath(child.path);
                      const childActive = isMenuItemActive(
                        activeMenuPath,
                        child.path,
                        childPaths ?? [],
                      );

                      return (
                        <ListItemButton
                          key={child.title}
                          onClick={() => navigate(childPath)}
                          sx={{
                            pl: 3.2,
                            mx: 1,
                            borderRadius: 1,
                            backgroundColor: childActive
                              ? '#2a2a2a'
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: '#2a2a2a',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{ color: '#E31E25', minWidth: '35px' }}
                          >
                            {' '}
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.title} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Divider />

      <Box sx={{ mt: 'auto', p: 1 }}>
        <Button
          fullWidth
          startIcon={<Logout />}
          onClick={onLogout}
          sx={{
            color: '#fff',
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': {
              backgroundColor: '#2a2a2a',
            },
          }}
        >
          {!collapsed && 'Logout V3.1.0'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default withUserAuth(Sidebar);
